import React, { useState, useMemo } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  IconButton,
  useColorModeValue,
  Avatar,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
} from '@chakra-ui/react';
import {
  FiHome,
  FiFileText,
  FiPackage,
  FiDollarSign,
  FiUsers,
  FiSettings,
  FiLogOut,
  FiGlobe,
  FiLayers,
  FiChevronDown,
  FiChevronRight,
  FiMenu,
  FiTool,
} from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNavigation } from '../contexts/NavigationContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';
import EntitySelector from './EntitySelector';

const MenuItemComponent = ({ icon, children, to, isActive, hideLabel }) => {
  const navigate = useNavigate();
  const { checkUnsavedChanges } = useNavigation();

  const handleClick = () => {
    const isBlocked = checkUnsavedChanges(navigate, to);
    if (isBlocked) {
      // Navigation blocked, don't navigate
      return;
    }
    navigate(to);
  };
  
  return (
    <Box
      as="button"
      w="100%"
      p={2}
      borderRadius="md"
      bg={isActive ? 'brand.500' : 'transparent'}
      color={isActive ? 'white' : 'gray.600'}
      _hover={{
        bg: isActive ? 'brand.600' : 'gray.100',
      }}
      onClick={handleClick}
      display="flex"
      alignItems="center"
      gap={2}
      textAlign="left"
      transition="all 0.2s"
      justifyContent={hideLabel ? 'center' : 'flex-start'}
    >
      {icon}
      {!hideLabel && <Text fontSize="sm">{children}</Text>}
    </Box>
  );
};

const SubMenuItem = ({ label, to, isActive }) => {
  const navigate = useNavigate();
  const { checkUnsavedChanges } = useNavigation();

  const handleClick = () => {
    if (checkUnsavedChanges(navigate, to)) {
      // Navigation blocked, don't navigate
      return;
    }
    navigate(to);
  };

  return (
    <Box
      as="button"
      w="100%"
      pl={7}
      pr={2}
      py={1.5}
      borderRadius="md"
      bg={isActive ? 'gray.100' : 'transparent'}
      color={isActive ? 'brand.600' : 'gray.600'}
      _hover={{ bg: 'gray.100' }}
      onClick={handleClick}
      textAlign="left"
      fontSize="sm"
    >
      {label}
    </Box>
  );
};

const Sidebar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const menuItems = [
    { icon: <FiHome />, label: t('nav.dashboard'), path: '/dashboard' },
    { icon: <FiLayers />, label: t('nav.project'), path: '/projects' },
    { icon: <FiFileText />, label: t('nav.proposals'), path: '/proposals' },
    { icon: <FiPackage />, label: t('nav.inventory'), path: '/inventory' },
    { icon: <FiTool />, label: t('services.title'), path: '/services' },
    { icon: <FiDollarSign />, label: t('nav.invoices'), path: '/invoices' },
    { icon: <FiUsers />, label: t('nav.suppliers'), path: '/suppliers' },
    { icon: <FiUsers />, label: t('clients.title'), path: '/clients' },
  ];

  const onSettingsRoute = useMemo(() => location.pathname.startsWith('/settings'), [location.pathname]);
  const [settingsOpen, setSettingsOpen] = useState(onSettingsRoute);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Box
      position="fixed"
      left={0}
      top={0}
      h="100vh"
      w={collapsed ? '64px' : '155px'}
      bg="white"
      borderRight="1px"
      borderColor="gray.200"
      zIndex={10}
      overflowY="auto"
    >
      <VStack h="100%" spacing={0}>
        {/* Logo */}
        <Box p={4} w="100%" borderBottom="1px" borderColor="gray.200" display="flex" alignItems="center" justifyContent="center">
          <Box flex={1} display="flex" alignItems="center" justifyContent={'center'}>
            <img src="/SP_Logo_v1.png" alt="SP" style={{ width: collapsed ? '36px' : '120px', height: 'auto' }} />
          </Box>
        </Box>

        {/* Menu Items */}
        <VStack flex={1} w="100%" p={3} spacing={1} align="stretch">
          {menuItems.map((item) => (
            <Tooltip key={item.path} label={collapsed ? item.label : ''} placement="right" openDelay={300}>
              <Box w="100%">
                <MenuItemComponent
                  icon={item.icon}
                  to={item.path}
                  isActive={location.pathname === item.path}
                  hideLabel={collapsed}
                >
                  {item.label}
                </MenuItemComponent>
              </Box>
            </Tooltip>
          ))}

          {/* Settings with nested submenu */}
          {collapsed ? (
            <Popover trigger="hover" placement="right-start" openDelay={0} closeDelay={150} gutter={8}>
              <PopoverTrigger>
                <Box
                  as="button"
                  w="100%"
                  p={2}
                  borderRadius="md"
                  bg={onSettingsRoute ? 'brand.500' : 'transparent'}
                  color={onSettingsRoute ? 'white' : 'gray.600'}
                  _hover={{ bg: onSettingsRoute ? 'brand.600' : 'gray.100' }}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  textAlign="left"
                  justifyContent={'center'}
                >
                  <FiSettings />
                </Box>
              </PopoverTrigger>
              <Portal>
                <PopoverContent w="220px" zIndex={2000} _focus={{ boxShadow: 'md' }}>
                  <PopoverBody p={2}>
                    <VStack w="100%" spacing={1} align="stretch">
                      <SubMenuItem label={t('settings.entities', { defaultValue: 'Entities' })} to="/settings/entities" isActive={location.pathname === '/settings/entities'} />
                      <SubMenuItem label={t('nav.users')} to="/settings/users" isActive={location.pathname === '/settings/users'} />
                      <SubMenuItem label={t('settings.language')} to="/settings/language" isActive={location.pathname === '/settings/language'} />
                      <SubMenuItem label={t('settings.sections')} to="/settings/sections" isActive={location.pathname === '/settings/sections'} />
                      <SubMenuItem label={t('settings.templates')} to="/settings/templates" isActive={location.pathname === '/settings/templates'} />
                      <SubMenuItem label={t('settings.pricing_formulas')} to="/settings/rules" isActive={location.pathname === '/settings/rules'} />
                      <SubMenuItem label={t('settings.general')} to="/settings/general" isActive={location.pathname === '/settings/general'} />
                      <SubMenuItem label={t('settings.pdf', { defaultValue: 'PDF Settings' })} to="/settings/pdf" isActive={location.pathname === '/settings/pdf'} />
                      <SubMenuItem label={t('settings.service_categories', { defaultValue: 'Service Categories' })} to="/settings/service-categories" isActive={location.pathname === '/settings/service-categories'} />
                    </VStack>
                  </PopoverBody>
                </PopoverContent>
              </Portal>
            </Popover>
          ) : (
            <Tooltip label={''} placement="right" openDelay={300}>
              <Box
                as="button"
                w="100%"
                p={2}
                borderRadius="md"
                bg={onSettingsRoute ? 'brand.500' : 'transparent'}
                color={onSettingsRoute ? 'white' : 'gray.600'}
                _hover={{ bg: onSettingsRoute ? 'brand.600' : 'gray.100' }}
                onClick={() => setSettingsOpen((v) => !v)}
                display="flex"
                alignItems="center"
                gap={2}
                textAlign="left"
                justifyContent={'flex-start'}
              >
                <FiSettings />
                <Text fontSize="sm" flex={1}>{t('nav.settings')}</Text>
                {settingsOpen ? <FiChevronDown /> : <FiChevronRight />}
              </Box>
            </Tooltip>
          )}
          {!collapsed && settingsOpen && (
            <VStack w="100%" spacing={1} align="stretch">
              <SubMenuItem label={t('settings.entities', { defaultValue: 'Entities' })} to="/settings/entities" isActive={location.pathname === '/settings/entities'} />
              <SubMenuItem label={t('nav.users')} to="/settings/users" isActive={location.pathname === '/settings/users'} />
              <SubMenuItem label={t('settings.language')} to="/settings/language" isActive={location.pathname === '/settings/language'} />
              <SubMenuItem label={t('settings.sections')} to="/settings/sections" isActive={location.pathname === '/settings/sections'} />
              <SubMenuItem label={t('settings.templates')} to="/settings/templates" isActive={location.pathname === '/settings/templates'} />
              <SubMenuItem label={t('settings.pricing_formulas')} to="/settings/rules" isActive={location.pathname === '/settings/rules'} />
              <SubMenuItem label={t('settings.general')} to="/settings/general" isActive={location.pathname === '/settings/general'} />
              <SubMenuItem label={t('settings.pdf', { defaultValue: 'PDF Settings' })} to="/settings/pdf" isActive={location.pathname === '/settings/pdf'} />
              <SubMenuItem label={t('settings.service_categories', { defaultValue: 'Service Categories' })} to="/settings/service-categories" isActive={location.pathname === '/settings/service-categories'} />
            </VStack>
          )}
        </VStack>

        {/* User Info & Language */}
        <Box p={3} w="100%" borderTop="1px" borderColor="gray.200">
          <VStack spacing={3}>
            <HStack justify="space-between" w="100%">
              <HStack>
                <Avatar size="xs" name={user?.full_name} />
                {!collapsed && (
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" fontWeight="medium">
                      {user?.full_name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {t(`users.role.${user?.role}`, { defaultValue: user?.role })}
                    </Text>
                  </VStack>
                )}
              </HStack>
              <IconButton
                icon={<FiLogOut />}
                variant="ghost"
                size="xs"
                onClick={handleLogout}
                aria-label={t('nav.logout')}
              />
            </HStack>
 
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

const Layout = ({ children }) => {
  const { t } = useLanguage();
  const bg = useColorModeValue('gray.50', 'gray.900');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebar_collapsed') || 'false'); } catch { return false; }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed((v) => { localStorage.setItem('sidebar_collapsed', JSON.stringify(!v)); return !v; });
  };
  React.useEffect(() => {
    const stored = localStorage.getItem('sidebar_collapsed');
    if (stored !== null) {
      try { setSidebarCollapsed(JSON.parse(stored)); } catch {}
    }
    const handler = (e) => {
      const next = !!(e && e.detail);
      setSidebarCollapsed(next);
      try { localStorage.setItem('sidebar_collapsed', JSON.stringify(next)); } catch {}
    };
    window.addEventListener('sidebar:set-collapsed', handler);
    return () => window.removeEventListener('sidebar:set-collapsed', handler);
  }, []);

  return (
    <Box minH="100vh" bg={bg}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
      
      <Box ml={sidebarCollapsed ? '64px' : '155px'} transition=".3s ease">
        <Flex
          as="header"
          align="center"
          justify="space-between"
          w="100%"
          px={5}
          py={3}
          bg="white"
          borderBottom="1px"
          borderColor="gray.200"
        >
          <HStack spacing={3}>
            <IconButton aria-label="toggle sidebar" size="sm" variant="ghost" icon={<FiMenu />} onClick={toggleSidebar} />
            <Text fontSize="sm" fontWeight="semibold">{t('app.title', { defaultValue: 'SP Proposal Management' })}</Text>
          </HStack>
          
          {/* Header'da dil ve entity seçici */}
          <HStack spacing={3}>
            <HStack spacing={2} align="center">
              <FiGlobe size={14} />
              <LanguageSelector size="sm" showLabel={false} />
            </HStack>
            <EntitySelector size="sm" showLabel={false} />
          </HStack>
        </Flex>

        <Box as="main" p={6}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
